'use client';

import { useOptionalMoviesListState } from '@/app/movies/components/movies-list-state-provider';
import { useSharedState, useUpdateSharedState } from '@/hooks/state/shared-state';
import { useTheaterContext, useUpdateTheaterContext } from '@/hooks/state/theater';
import { useTheatersContext, useUpdateTheatersContext } from '@/hooks/state/theaters';
import { MovieCatalogLocation } from '@/types/movie-catalog';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { useState } from 'react';

type UseMoviesNearbyTheatersResult = {
  clearZipCode: () => void;
  handleTheaterResults: (results: PagedListOfTheater, isGeoSearch?: boolean, searchedZipCode?: string) => void;
  isLocationApplied: boolean;
  location: MovieCatalogLocation;
  zipCode?: string;
};

export const useMoviesNearbyTheaters = (): UseMoviesNearbyTheatersResult => {
  const [locallyClearedLocationFilter, setLocallyClearedLocationFilter] = useState(false);
  const [searchedZipCode, setSearchedZipCode] = useState<string>();
  const moviesListState = useOptionalMoviesListState();
  const { data: sharedState } = useSharedState();
  const { data: theater } = useTheaterContext();
  const { data: theatersContext } = useTheatersContext();
  const { mutate: updateSharedState } = useUpdateSharedState();
  const { mutate: persistInitialMoviesLocation } = useUpdateSharedState(false);
  const { mutate: updateTheaterContext } = useUpdateTheaterContext();
  const { mutate: updateTheatersContext } = useUpdateTheatersContext();

  const isZipCodeCleared = moviesListState?.hasClearedLocationFilter ?? locallyClearedLocationFilter;
  const setHasClearedLocationFilter = moviesListState?.setHasClearedLocationFilter ?? setLocallyClearedLocationFilter;
  const sharedZipCode = sharedState?.lastTextSearch?.toString() || theater?.zipcode;
  const selectedZipCode = isZipCodeCleared ? undefined : sharedZipCode;
  const [latitude, longitude] = isZipCodeCleared ? [] : sharedState?.lastGeoPosition?.split(',').map(Number) || [];
  const zipCode = searchedZipCode || (!isZipCodeCleared ? selectedZipCode || theater?.zipcode : undefined);
  const catalogZipCode = searchedZipCode || (isZipCodeCleared ? sharedZipCode : selectedZipCode);
  const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const hasSharedLocation = !!sharedZipCode || hasValidCoordinates || !!theatersContext?.data?.length;
  const location: MovieCatalogLocation = catalogZipCode
    ? {
      latitude: undefined,
      longitude: undefined,
      ...isZipCodeCleared ? { preserveCategoryMembership: true } : {},
      zipCode: catalogZipCode,
    }
    : hasValidCoordinates
      ? { latitude, longitude, zipCode: undefined }
      : { latitude: undefined, longitude: undefined, zipCode: undefined };
  const isLocationApplied = !isZipCodeCleared && (!!zipCode || hasValidCoordinates || !!theatersContext?.data?.length);

  const handleTheaterResults = (results: PagedListOfTheater, isGeoSearch = false, zipCodeInput?: string): void => {
    setHasClearedLocationFilter(false);
    setSearchedZipCode(zipCodeInput);
    if (!isGeoSearch) {
      if (!hasSharedLocation && zipCodeInput) {
        persistInitialMoviesLocation({ lastTextSearch: parseInt(zipCodeInput, 10) });
      }
      return;
    }

    const selectedTheater = results.data?.[0];

    if (selectedTheater) {
      updateTheaterContext(selectedTheater);
    }
    updateTheatersContext(results);

    updateSharedState(selectedTheater
      ? { lastTextSearch: null, lastViewedTheaterId: selectedTheater.theaterId }
      : { lastTextSearch: null }
    );
  };

  const clearZipCode = (): void => {
    setHasClearedLocationFilter(true);
    setSearchedZipCode(undefined);
  };

  return {
    clearZipCode,
    handleTheaterResults,
    isLocationApplied,
    location,
    zipCode,
  };

};
